/**
 * MINI Power Steering Pump Compatibility Checker
 * ─────────────────────────────────────────────────
 * Checks whether a UK-registered vehicle is a first-generation BMW MINI
 * (R50/R52/R53, petrol, Mark 1).
 *
 * API calls go through the Cloudflare Worker proxy — the live API key
 * is held as a Worker secret and is never present in this file or in
 * the page source.
 *
 * Initialised from the Liquid section via window.MiniCheckerQueue.
 */

(function () {
  'use strict';

  // ─── Match logic ──────────────────────────────────────────────────────────

  /**
   * First-generation petrol MINI chassis codes.
   *   R50 = hatchback (base/One/Cooper)
   *   R53 = hatchback Cooper S
   *   R52 = convertible
   * The API returns these in two places:
   *   ModelData.Series   → individual code: "R50", "R52", "R53"
   *   SmmtDetails.Series → grouped code:    "R50/53" (hatchbacks)
   */
  const VALID_SERIES = new Set(['R50', 'R52', 'R53', 'R50/53', 'R50/R53']);

  function isFirstGenMini(data) {
    const md   = data.ModelData   || {};
    const smmt = data.SmmtDetails || {};

    const modelSeries = (md.Series   || '').toUpperCase().trim();
    const smmtSeries  = (smmt.Series || '').toUpperCase().trim();
    const seriesOk    = VALID_SERIES.has(modelSeries) || VALID_SERIES.has(smmtSeries);

    const fuelOk = (md.FuelType   || '').toUpperCase().trim() === 'PETROL' ||
                   (smmt.FuelType || '').toUpperCase().trim() === 'PETROL';

    const markOk = Number(md.Mark) === 1;

    return seriesOk && fuelOk && markOk;
  }

  function extractDetails(data) {
    const vi  = data.VehicleIdentification || {};
    const md  = data.ModelData             || {};
    const col = data.ColourDetails         || {};
    const c   = col.CurrentColour || '';

    return {
      colour: c.charAt(0).toUpperCase() + c.slice(1).toLowerCase(),
      make:   vi.DvlaMake || 'MINI',
      model:  md.Model    || vi.DvlaModel || '',
      vin:    vi.Vin      || '',
    };
  }

  // ─── Formatting ───────────────────────────────────────────────────────────

  function normalizeReg(reg) {
    return (reg || '').replace(/\s+/g, '').toUpperCase();
  }

  function formatReg(reg) {
    const r = normalizeReg(reg);
    if (r.length === 7) return r.slice(0, 4) + ' ' + r.slice(4);
    if (r.length === 6) return r.slice(0, 3) + ' ' + r.slice(3);
    return r;
  }

  // ─── Main class ───────────────────────────────────────────────────────────

  class MiniCompatibilityChecker {
    constructor(cfg) {
      this.cfg = {
        proxyUrl:     cfg.proxyUrl     || '',
        phone:        cfg.phone        || '01636 337339',
        phoneRaw:    (cfg.phone        || '01636337339').replace(/\s+/g, ''),
        contactUrl:   cfg.contactUrl   || 'https://ljsas.co.uk/pages/get-in-touch',
        contactLabel: cfg.contactLabel || 'contact form',
        sectionId:    cfg.sectionId    || '',
      };

      const id = this.cfg.sectionId ? '-' + this.cfg.sectionId : '';

      this.els = {
        form:       document.getElementById('mini-checker-form'        + id),
        result:     document.getElementById('mini-checker-result'      + id),
        regInput:   document.getElementById('mini-checker-reg'         + id),
        submitBtn:  document.getElementById('mini-checker-submit'      + id),
        vinToggle:  document.getElementById('mini-checker-vin-toggle'  + id),
        vinSection: document.getElementById('mini-checker-vin-section' + id),
        vinInput:   document.getElementById('mini-checker-vin'         + id),
      };

      this._init();
    }

    _init() {
      const { form, regInput, vinToggle, vinSection } = this.els;

      if (form) {
        form.addEventListener('submit', e => {
          e.preventDefault();
          this._check();
        });
      }

      if (regInput) {
        regInput.addEventListener('input', () => {
          const pos = regInput.selectionStart;
          regInput.value = regInput.value.toUpperCase().replace(/[^A-Z0-9\s]/g, '');
          regInput.setSelectionRange(pos, pos);
        });
      }

      if (vinToggle && vinSection) {
        vinToggle.addEventListener('change', () => {
          vinSection.hidden = !vinToggle.checked;
        });
      }
    }

    // ── Lookup ──────────────────────────────────────────────────────────────

    async _check() {
      const reg = normalizeReg(this.els.regInput?.value || '');
      if (!reg) { this.els.regInput?.focus(); return; }

      const vinLast = (this.els.vinInput?.value || '').replace(/\s+/g, '').toUpperCase();

      this._setLoading(true);
      this._clearResult();

      try {
        const data    = await this._fetchViaProxy(reg);
        const match   = isFirstGenMini(data);
        const details = extractDetails(data);

        // Optional VIN cross-check
        let vinMismatch = false;
        if (vinLast.length > 0 && details.vin) {
          vinMismatch = details.vin.slice(-vinLast.length).toUpperCase() !== vinLast;
        }

        if (match && !vinMismatch) {
          this._showSuccess(details, reg);
        } else if (match && vinMismatch) {
          this._showVinMismatch(reg);
        } else {
          this._showNoMatch(reg);
        }
      } catch (err) {
        if (err.notFound) {
          this._showNoMatch(reg);
        } else if (err.status === 429) {
          this._showRateLimited();
        } else {
          this._showError();
        }
      } finally {
        this._setLoading(false);
      }
    }

    /**
     * Calls the Cloudflare Worker proxy endpoint.
     * The live API key never touches the browser — it lives in the Worker.
     */
    async _fetchViaProxy(vrm) {
      if (!this.cfg.proxyUrl) {
        throw new Error('Proxy URL not configured');
      }

      const url = `${this.cfg.proxyUrl.replace(/\/$/, '')}/lookup?vrm=${encodeURIComponent(vrm)}`;

      let res;
      try {
        res = await fetch(url, { signal: AbortSignal.timeout(12000) });
      } catch (err) {
        throw Object.assign(new Error(`Network error: ${err.message}`), { isNetwork: true });
      }

      if (res.status === 404) {
        // Worker signals "vehicle not found" — treat as no-match, not an error
        const e = new Error('Vehicle not found');
        e.notFound = true;
        throw e;
      }

      if (!res.ok) {
        const e = new Error(`Proxy returned ${res.status}`);
        e.status = res.status;
        throw e;
      }

      const data = await res.json();

      if (!data.VehicleIdentification?.Vrm) {
        throw new Error('Unexpected proxy response shape');
      }

      return data;
    }

    // ── Result display ───────────────────────────────────────────────────────

    _showSuccess({ colour, make, model }, reg) {
      const label = [colour, make, model].filter(Boolean).join(' ');
      this._render(`
        <div class="mini-result mini-result--success" role="alert">
          <div class="mini-result__icons" aria-hidden="true">✓</div>
          <p class="mini-result__message">
            All of our products are suitable for your
            <strong>${this._esc(label)}</strong>,
            registration <strong>${this._esc(formatReg(reg))}</strong>.
            You can be totally confident ordering anything from our store.
          </p>
        </div>`);
    }

    _showNoMatch(reg) {
      this._render(`
        <div class="mini-result mini-result--fail" role="alert">
          <div class="mini-result__icons" aria-hidden="true">✗</div>
          <p class="mini-result__message">
            Unfortunately <strong>${this._esc(formatReg(reg))}</strong> doesn't appear to be a first generation BMW MINI.
            Please call us on
            <a href="tel:${this._esc(this.cfg.phoneRaw)}">${this._esc(this.cfg.phone)}</a>
            or use our
            <a href="${this._esc(this.cfg.contactUrl)}">${this._esc(this.cfg.contactLabel)}</a>
            if you wish to follow up.
          </p>
        </div>`);
    }

    _showError() {
      this._render(`
        <div class="mini-result mini-result--error" role="alert">
          <div class="mini-result__icons" aria-hidden="true">⚠</div>
          <p class="mini-result__message">
            Sorry, our registration lookup is temporarily unavailable.
            Please try again shortly or contact us on
            <a href="tel:${this._esc(this.cfg.phoneRaw)}">${this._esc(this.cfg.phone)}</a>
            /
            <a href="${this._esc(this.cfg.contactUrl)}">${this._esc(this.cfg.contactLabel)}</a>.
          </p>
        </div>`);
    }

    _showVinMismatch(reg) {
      this._render(`
        <div class="mini-result mini-result--fail" role="alert">
          <div class="mini-result__icons" aria-hidden="true">✗</div>
          <p class="mini-result__message">
            The registration <strong>${this._esc(formatReg(reg))}</strong> was found,
            but the VIN digits you entered don't match our records.
            Please double-check your logbook, or call us on
            <a href="tel:${this._esc(this.cfg.phoneRaw)}">${this._esc(this.cfg.phone)}</a>
            or use our
            <a href="${this._esc(this.cfg.contactUrl)}">${this._esc(this.cfg.contactLabel)}</a>.
          </p>
        </div>`);
    }

    _showRateLimited() {
      this._render(`
        <div class="mini-result mini-result--error" role="alert">
          <div class="mini-result__icons" aria-hidden="true">⚠</div>
          <p class="mini-result__message">
            You've made several lookups in quick succession — please wait a moment and try again.
          </p>
        </div>`);
    }

    // ── Helpers ──────────────────────────────────────────────────────────────

    _render(html) {
      if (this.els.result) this.els.result.innerHTML = html;
    }

    _clearResult() {
      if (this.els.result) this.els.result.innerHTML = '';
    }

    _setLoading(on) {
      const btn = this.els.submitBtn;
      if (!btn) return;
      btn.disabled    = on;
      btn.textContent = on ? 'Checking…' : 'Check Compatibility';
    }

    /** Minimal HTML escaping — prevents XSS from API-returned strings. */
    _esc(str) {
      return String(str)
        .replace(/&/g,  '&amp;')
        .replace(/</g,  '&lt;')
        .replace(/>/g,  '&gt;')
        .replace(/"/g,  '&quot;')
        .replace(/'/g,  '&#39;');
    }
  }

  // ─── Bootstrap ────────────────────────────────────────────────────────────

  window.MiniCompatibilityChecker = MiniCompatibilityChecker;

  if (Array.isArray(window.MiniCheckerQueue)) {
    window.MiniCheckerQueue.forEach(cfg => new MiniCompatibilityChecker(cfg));
    window.MiniCheckerQueue = [];
  }

})();
